import { StarFour } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twTransform } from "@/lib/emotionTw";

export type StepProgressState = "done" | "active" | "pending" | "disabled";

export type StepProgressStep = {
  id?: string;
  label: ReactNode;
  status?: StepProgressState;
  disabled?: boolean;
};

export type StepProgressCardProps = HTMLAttributes<HTMLElement> & {
  eyebrow?: ReactNode;
  title?: ReactNode;
  steps: StepProgressStep[];
  currentStep?: number;
  surface?: "card" | "plain";
  animated?: boolean;
};

/* Exact compiled output of the pre-migration Tailwind utilities plus the
 * shared `.bfrs-faded-card-border` component-layer rules (copied verbatim
 * from Card.tsx / the frozen baseline).
 *
 * The original root class list also carried `bfrs-[word-break:break-word]`,
 * a pre-existing DEAD arbitrary-property class (broken by the `bfrs-` prefix,
 * absent from the compiled stylesheet) — intentionally not reproduced. */

const fadedCardBorder: CSSObject = {
  position: "relative",
  border: "1px solid transparent",
  isolation: "isolate",
  "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border)",
  "&:before": {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    padding: "1px",
    pointerEvents: "none",
    content: '""',
    background: "var(--bfrs-card-border-gradient)",
    borderRadius: "inherit",
    WebkitMask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    mask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude"
  },
  "&:hover": {
    "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border-hover)"
  }
};

/* Keyframe bodies are byte-for-byte the compiled stylesheet output. */

const kfProgressActiveStar: CSSObject = {
  "@keyframes bfrs-bfrs-progress-active-star": {
    "0%": { transform: "rotate(0) scale(1)" },
    "58%": { transform: "rotate(720deg) scale(1.08)" },
    to: { transform: "rotate(720deg) scale(1)" }
  }
};

const kfProgressStepDone: CSSObject = {
  "@keyframes bfrs-bfrs-progress-step-done": {
    "0%": { transform: "scale(.7)", opacity: 0 },
    "60%": { transform: "scale(1.18)", opacity: 1 },
    to: { transform: "scale(1)", opacity: 1 }
  }
};

const kfProgressRailFill: CSSObject = {
  "@keyframes bfrs-bfrs-progress-rail-fill": {
    "0%": { transform: "scaleY(0)", transformOrigin: "top" },
    to: { transform: "scaleY(1)", transformOrigin: "top" }
  }
};

const StepProgressRoot = styled("section", {
  label: "AgenticStepProgressCard-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { surface: "card" | "plain" } }>(({ ownerState }) => [
  {
    width: "100%",
    maxWidth: "425px"
  },
  ownerState.surface === "card" && {
    ...fadedCardBorder,
    borderRadius: "var(--bfrs-radius-xl)",
    backgroundImage: "var(--bfrs-gradient-card)",
    padding: "1.25rem",
    "--tw-shadow": "var(--bfrs-shadow-card)",
    "--tw-shadow-colored": "var(--bfrs-shadow-card)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  }
]);

const StepProgressHeader = styled("div", {
  label: "AgenticStepProgressCard-header"
})({
  marginBottom: "21px"
});

const StepProgressEyebrow = styled("p", {
  label: "AgenticStepProgressCard-eyebrow"
})({
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  lineHeight: 1.5,
  letterSpacing: "1.6px",
  color: "var(--bfrs-dcl-color-step-progress-eyebrow)"
});

const StepProgressTitle = styled("h3", {
  label: "AgenticStepProgressCard-title"
})({
  marginTop: ".5rem",
  fontSize: "24px",
  fontWeight: 700,
  lineHeight: 1.5,
  color: "var(--bfrs-dcl-color-step-progress-title)"
});

const StepProgressList = styled("ol", {
  label: "AgenticStepProgressCard-list"
})({
  display: "grid",
  gap: "0px"
});

const StepProgressItem = styled("li", {
  label: "AgenticStepProgressCard-item"
})({
  display: "grid",
  minHeight: "35px",
  gridTemplateColumns: "16px minmax(0,1fr)",
  gap: ".5rem"
});

const StepProgressMarkerColumn = styled("div", {
  label: "AgenticStepProgressCard-markerColumn",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { active: boolean } }>(({ ownerState }) => [
  {
    position: "relative",
    display: "flex",
    justifyContent: "center"
  },
  ownerState.active ? { paddingTop: 0 } : { paddingTop: "3px" }
]);

const StepProgressActiveMarker = styled("span", {
  label: "AgenticStepProgressCard-activeMarker",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { animated: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "1rem",
    height: "1rem",
    transformOrigin: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--bfrs-dcl-color-step-progress-active-marker)"
  },
  ownerState.animated && {
    ...kfProgressActiveStar,
    animation: "bfrs-bfrs-progress-active-star 2.6s ease-in-out infinite"
  }
]);

const StepProgressDotMarker = styled("span", {
  label: "AgenticStepProgressCard-dotMarker",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { state: StepProgressState; animated: boolean } }>(({ ownerState }) => [
  {
    display: "block",
    width: ".5rem",
    height: ".5rem",
    borderRadius: "var(--bfrs-radius-pill)",
    transitionProperty: "color,background-color,border-color,text-decoration-color,fill,stroke",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".3s"
  },
  ownerState.state === "done" && {
    backgroundColor: "var(--bfrs-dcl-color-step-progress-done-marker)"
  },
  ownerState.state === "pending" && {
    backgroundColor: "var(--bfrs-dcl-color-step-progress-pending)"
  },
  ownerState.state === "disabled" && {
    backgroundColor: "var(--bfrs-dcl-color-step-progress-pending)",
    opacity: ".6"
  },
  ownerState.animated &&
    ownerState.state === "done" && {
      ...kfProgressStepDone,
      animation: "bfrs-bfrs-progress-step-done var(--bfrs-motion-slow) var(--bfrs-ease-out)"
    }
]);

const StepProgressRail = styled("span", {
  label: "AgenticStepProgressCard-rail",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { activeStep: boolean; nextActive: boolean } }>(({ ownerState }) => [
  {
    position: "absolute",
    left: "50%",
    width: "1px",
    ...twTransform({ "--tw-translate-x": "-50%" }),
    overflow: "hidden",
    backgroundColor: "var(--bfrs-dcl-color-step-progress-pending)"
  },
  ownerState.activeStep ? { top: "22px" } : { top: "17px" },
  ownerState.nextActive ? { height: "calc(100% - 17px)" } : { height: "calc(100% - 10px)" }
]);

const StepProgressRailFill = styled("span", {
  label: "AgenticStepProgressCard-railFill",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { done: boolean; animated: boolean } }>(({ ownerState }) => [
  {
    display: "block",
    height: "100%",
    width: "100%",
    backgroundColor: "var(--bfrs-dcl-color-step-progress-done-marker)",
    transitionProperty: "color,background-color,border-color,text-decoration-color,fill,stroke",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".3s"
  },
  ownerState.done ? { opacity: 1 } : { opacity: 0 },
  ownerState.animated &&
    ownerState.done && {
      ...kfProgressRailFill,
      animation: "bfrs-bfrs-progress-rail-fill .38s var(--bfrs-ease-out) both"
    }
]);

const StepProgressTextColumn = styled("div", {
  label: "AgenticStepProgressCard-textColumn"
})({
  paddingBottom: "1rem"
});

const StepProgressLabel = styled("p", {
  label: "AgenticStepProgressCard-label",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { state: StepProgressState } }>(({ ownerState }) => [
  {
    whiteSpace: "normal",
    fontSize: "12px",
    lineHeight: 1.5,
    transitionProperty: "all",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".3s"
  },
  ownerState.state === "done" && {
    fontWeight: 400,
    color: "var(--bfrs-dcl-color-step-progress-done-text)"
  },
  ownerState.state === "active" && {
    fontWeight: 500,
    color: "var(--bfrs-dcl-color-step-progress-active-text)"
  },
  (ownerState.state === "pending" || ownerState.state === "disabled") && {
    fontWeight: 400,
    color: "var(--bfrs-dcl-color-step-progress-pending)"
  }
]);

function getStepState(step: StepProgressStep, index: number, currentStep: number): StepProgressState {
  if (step.disabled) return "disabled";
  if (step.status) return step.status;
  if (index < currentStep) return "done";
  if (index === currentStep) return "active";
  return "pending";
}

function StepMarker({ state, animated }: { state: StepProgressState; animated: boolean }) {
  if (state === "active") {
    return (
      <StepProgressActiveMarker ownerState={{ animated }}>
        <StarFour size={14} weight="fill" />
      </StepProgressActiveMarker>
    );
  }

  return <StepProgressDotMarker ownerState={{ state, animated }} />;
}

export const StepProgressCard = forwardRef<HTMLElement, StepProgressCardProps>(
  ({ className, eyebrow = "Identity & Contact", title = "Getting your details ready…", steps, currentStep = 0, surface = "plain", animated = true, ...props }, ref) => {
    const resolvedSteps = steps.map((step, index) => ({
      ...step,
      state: getStepState(step, index, currentStep)
    }));

    return (
      <StepProgressRoot
        ref={ref}
        ownerState={{ surface }}
        className={className}
        aria-label={typeof eyebrow === "string" ? eyebrow : "Progress"}
        {...props}
      >
        {(eyebrow || title) && (
          <StepProgressHeader>
            {eyebrow && <StepProgressEyebrow>{eyebrow}</StepProgressEyebrow>}
            {title && <StepProgressTitle>{title}</StepProgressTitle>}
          </StepProgressHeader>
        )}

        <StepProgressList aria-live="polite">
          {resolvedSteps.map((step, index) => {
            const nextStep = resolvedSteps[index + 1];
            const connectorState = step.state === "done" ? "done" : step.state === "active" && nextStep?.state === "done" ? "done" : "pending";
            const nextIsActive = nextStep?.state === "active";
            const isActive = step.state === "active";

            return (
              <StepProgressItem key={step.id ?? index}>
                <StepProgressMarkerColumn ownerState={{ active: isActive }}>
                  <StepMarker state={step.state} animated={animated} />
                  {index < resolvedSteps.length - 1 && (
                    <StepProgressRail ownerState={{ activeStep: isActive, nextActive: Boolean(nextIsActive) }}>
                      <StepProgressRailFill ownerState={{ done: connectorState === "done", animated }} />
                    </StepProgressRail>
                  )}
                </StepProgressMarkerColumn>
                <StepProgressTextColumn>
                  <StepProgressLabel ownerState={{ state: step.state }}>{step.label}</StepProgressLabel>
                </StepProgressTextColumn>
              </StepProgressItem>
            );
          })}
        </StepProgressList>
      </StepProgressRoot>
    );
  }
);

StepProgressCard.displayName = "StepProgressCard";
